import type { JSONValue } from '@ur-solutions/react-hooks'
import { endOfMonth, startOfMonth } from 'date-fns'
import type { FilterDateRange } from './date/last-days-range'
import { getLastDaysRange, isValidLastDays } from './date/last-days-range'
import { isInvalidDate, isValidDateRange } from './time'

type JsonDateRange = { from: string; to: string; lastDays?: JSONValue }
type UserStorageState = { user?: unknown } | null

export type InitialUserSelectionSource = 'url' | 'stored' | 'fallback'

const isJsonTuple2 = (value: JSONValue): value is [JSONValue, JSONValue] =>
  Array.isArray(value) && value.length === 2

const isJsonDateRange = (value: JSONValue): value is JsonDateRange => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  if (!('from' in value) || !('to' in value)) return false
  return typeof value.from === 'string' && typeof value.to === 'string'
}

/*
 * String transforms
 */

export const asString = (value: JSONValue): string | undefined =>
  typeof value === 'string' ? value : undefined

export const asNullableString = (
  value: JSONValue
): string | null | undefined => {
  if (value === null) return null
  return asString(value)
}

/*
 * Boolean transforms
 */

export const asBoolean = (value: JSONValue): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined

export const asBooleanOrDefault = (
  value: JSONValue,
  defaultValue: boolean
): boolean => asBoolean(value) ?? defaultValue

/*
 * Array transforms
 */

export const asStringArray = (value: JSONValue): string[] | undefined => {
  if (!Array.isArray(value)) return undefined
  if (!value.every((item): item is string => typeof item === 'string'))
    return undefined
  return value
}

export const asStringArrayOrEmpty = (value: JSONValue): string[] =>
  asStringArray(value) ?? []

/*
 * Enum transforms
 */

export const asEnumValue = <TValues extends readonly string[]>(
  value: JSONValue,
  values: TValues
): TValues[number] | undefined => {
  if (typeof value !== 'string') return undefined
  return values.some(v => v === value) ? value : undefined
}

export const asEnumArrayOrEmpty = <TValues extends readonly string[]>(
  value: JSONValue,
  values: TValues
): TValues[number][] => {
  if (!Array.isArray(value)) return []

  return value.filter(
    (item): item is TValues[number] =>
      typeof item === 'string' && values.some(v => v === item)
  )
}

export const asEnumValueOrEmpty = <TValues extends readonly string[]>(
  value: JSONValue,
  values: TValues
): '' | TValues[number] | undefined => {
  if (value === '') return ''
  return asEnumValue(value, values)
}

/*
 * Date transforms
 */

export const asDate = (value: JSONValue): Date | undefined => {
  if (value instanceof Date) return isInvalidDate(value) ? undefined : value
  if (typeof value !== 'string' && typeof value !== 'number') return undefined

  const date = new Date(value)
  return isInvalidDate(date) ? undefined : date
}

export const asDateOrDefault = (value: JSONValue, fallback: Date): Date =>
  asDate(value) ?? fallback

export const asDateTuple = (value: JSONValue): [Date, Date] | undefined => {
  if (!isJsonTuple2(value)) return undefined

  const [startRaw, endRaw] = value
  const start = asDate(startRaw)
  const end = asDate(endRaw)

  if (!start || !end) return undefined
  return [start, end]
}

/*
 * Date range transforms
 */

const createDefaultDateRange = (): FilterDateRange => ({
  from: startOfMonth(new Date()),
  to: endOfMonth(new Date()),
})

export const parseDateRangeOrDefault = (value: JSONValue): FilterDateRange => {
  if (!isJsonDateRange(value)) return createDefaultDateRange()
  if (isValidLastDays(value.lastDays)) return getLastDaysRange(value.lastDays)

  const from = new Date(value.from)
  const to = new Date(value.to)

  return isValidDateRange({ from, to })
    ? { from, to }
    : createDefaultDateRange()
}

/*
 * User selection transforms
 */

export const getStoredUserId = (
  storedState: UserStorageState
): string | null =>
  storedState && typeof storedState.user === 'string' && storedState.user
    ? storedState.user
    : null

/**
 * Picks which user id to use when the filter is first initialized.
 *
 * Order:
 * 1) Use user id from URL if it is valid.
 * 2) Otherwise use user id from stored filter state.
 * 3) Otherwise use fallback user id (usually current user).
 */
export const resolveInitialUserSelection = ({
  routeUserId,
  routeUserIsValid,
  storedUserId,
  fallbackUserId,
}: {
  routeUserId?: string | null
  routeUserIsValid: boolean
  storedUserId: string | null
  fallbackUserId: string
}): { userId: string; source: InitialUserSelectionSource } => {
  if (routeUserId && routeUserIsValid)
    return { userId: routeUserId, source: 'url' }

  if (storedUserId) return { userId: storedUserId, source: 'stored' }

  return { userId: fallbackUserId, source: 'fallback' }
}
